import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../../helpers/element.helper';
import { DraggableComponent } from '../draggable/draggable.component';
import { DropZoneComponent } from '../drop-zone/drop-zone.component';
import { DraggableComponentEvent } from '../shared/events/drag-drop-component.event';
import {
  DRAGGABLE_SELECTED,
  DROPZONE_SELECTED,
  EXTERNAL_DROPZONE_SELECTED,
} from '../shared/events/drag-drop.events';
import { DragDropService } from '../shared/services/drag-drop.service';

interface SelectableElement extends HTMLElement {
  setSelected(selected: boolean): void;
}

@customElement(tagName('drag-drop'))
export class DragDropComponent extends TmElement {
  #dragDropService = new DragDropService(this);

  #mutationObserver?: MutationObserver;

  #draggableSelected: DraggableComponent | null = null;

  #dropzoneSelected: DropZoneComponent | null = null;

  #externalDropzoneSelected: HTMLElement | null = null;

  @property({ type: Boolean, reflect: true })
  set useDropper(useDropper: boolean) {
    if (useDropper) {
      this.#dragDropService.enableDropper();
    } else {
      this.#dragDropService.disableDropper();
    }
  }

  // TODO: this property should be removed when dropzone swapping doesn't cause any issues in the arrange tool
  @property({ type: Boolean })
  disableSwapping = false;

  get dragDropService() {
    return this.#dragDropService;
  }

  dropZoneComponents?: Array<DropZoneComponent>;

  standaloneDraggableComponents!: NodeListOf<DraggableComponent>;

  protected override firstUpdated() {
    if (!this.disableSwapping) {
      this.#setUpListeners();
    }

    this.dropZoneComponents?.forEach((dropZone) =>
      this.#dragDropService.registerDropZone(dropZone),
    );
  }

  protected override updated() {
    this.standaloneDraggableComponents = this.querySelectorAll(
      ':not(a-drop-zone) > a-draggable',
    );

    this.#dragDropService.registerStandaloneDraggables([
      ...this.standaloneDraggableComponents,
    ]);

    this.querySelectorAll('a-drop-zone').forEach((dropZone) => {
      this.#dragDropService.registerDropZone(dropZone);
    });

    this.querySelectorAll(
      'tmwc-content-drop-area, tmwc-content-gap-match-item',
    ).forEach((dropArea) => {
      if (dropArea.shadowRoot) {
        const observer = new MutationObserver(() => {
          dropArea.shadowRoot
            ?.querySelectorAll('a-drop-zone')
            .forEach((nestedDropZone) => {
              this.#dragDropService.registerDropZone(nestedDropZone);
            });
        });

        observer.observe(dropArea.shadowRoot, {
          childList: true,
        });
      }
    });
  }

  override async connectedCallback() {
    super.connectedCallback();

    this.#mutationObserver = new MutationObserver(
      this.#handleMutation.bind(this),
    );
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }

  #handleMutation(mutations: Array<MutationRecord>) {
    const newStandaloneDraggables: Array<DraggableComponent> = [];
    const removedStandaloneDraggables: Array<DraggableComponent> = [];
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof DropZoneComponent) {
          this.#dragDropService.registerDropZone(node);
        }

        if (
          node instanceof DraggableComponent &&
          !(node.parentNode instanceof DropZoneComponent)
        ) {
          newStandaloneDraggables.push(node);
        }
      });

      mutation.removedNodes.forEach((node) => {
        if (node instanceof DropZoneComponent) {
          this.#dragDropService.unregisterDropZone(node);
        }

        if (
          node instanceof DraggableComponent &&
          !(node.parentNode instanceof DropZoneComponent)
        ) {
          removedStandaloneDraggables.push(node);
        }
      });
    });

    this.#dragDropService.registerStandaloneDraggables(newStandaloneDraggables);
    this.#dragDropService.unregisterStandaloneDraggables(
      removedStandaloneDraggables.filter(
        (node) => !newStandaloneDraggables.includes(node),
      ),
    );
  }

  #setUpListeners() {
    this.addEventListener(
      DRAGGABLE_SELECTED,
      this.#onDraggableSelected.bind(this),
    );
    this.addEventListener(
      DROPZONE_SELECTED,
      this.#onDropzoneSelected.bind(this),
    );
    this.addEventListener(
      EXTERNAL_DROPZONE_SELECTED,
      this.#onExternalDropzoneSelected.bind(this),
    );
  }

  #isSelectableElement(element: HTMLElement): element is SelectableElement {
    return 'setSelected' in element;
  }

  #onDraggableSelected(event: Event) {
    const draggableEvent = event as CustomEvent;

    if (this.#checkForDropzones(draggableEvent.detail)) {
      this.#swapDraggables(draggableEvent.detail);
    } else {
      this.#draggableSelected?.setSelected(false);
      this.#draggableSelected = draggableEvent.detail;
      this.#draggableSelected?.setSelected(true);

      this.#checkSelections();
      this.#checkExternalDropzoneSelection();
    }
  }

  #checkForDropzones(secondDraggable: DraggableComponent) {
    const firstDropzone = this.#draggableSelected?.parentNode;
    const secondDropzone = secondDraggable?.parentNode;

    return (
      (firstDropzone instanceof DropZoneComponent && secondDropzone) ||
      (secondDropzone instanceof DropZoneComponent && firstDropzone)
    );
  }

  #onDropzoneSelected(event: Event) {
    const dropZoneEvent = event as CustomEvent;
    const dropZone = dropZoneEvent.detail;

    this.#dropzoneSelected?.setSelected(false);
    dropZone.setSelected(true);

    if (this.#dropzoneSelected) {
      this.#swapDropzoneContents(dropZone);
    } else {
      this.#dropzoneSelected = dropZone;
      this.#checkSelections();
      this.#checkExternalDropzoneSelection();
    }
  }

  #onExternalDropzoneSelected(event: Event) {
    const externalDropZoneEvent = event as CustomEvent;

    this.#externalDropzoneSelected = externalDropZoneEvent.detail;

    if (
      this.#externalDropzoneSelected &&
      this.#isSelectableElement(this.#externalDropzoneSelected)
    ) {
      this.#externalDropzoneSelected.setSelected(true);
    }

    this.#checkExternalDropzoneSelection();
  }

  #swapDraggables(secondDraggable: DraggableComponent) {
    if (!this.#draggableSelected) {
      return;
    }

    const firstDropzone = this.#draggableSelected.parentNode;
    const secondDropzone = secondDraggable.parentNode;

    if (firstDropzone instanceof DropZoneComponent) {
      firstDropzone.broadCastEvent('limitExceeded', this.#draggableSelected);
      firstDropzone.dropzoneService.setValue(secondDraggable || null);
    }

    if (secondDropzone instanceof DropZoneComponent) {
      secondDropzone.broadCastEvent('limitExceeded', this.#draggableSelected);
      secondDropzone.dropzoneService.setValue(this.#draggableSelected || null);
      secondDropzone.setSelected(false);
    }

    this.#resetSelected();
  }

  #checkExternalDropzoneSelection() {
    if (this.#externalDropzoneSelected && this.#dropzoneSelected) {
      const draggable = this.#dropzoneSelected.draggableComponents[0];
      if (draggable) {
        if (draggable.hasDuplicates) {
          draggable.remove();
        } else {
          this.#externalDropzoneSelected.appendChild(draggable);
        }
        this.#dropzoneSelected.dispatchEvent(
          new DraggableComponentEvent('draggable-left', draggable),
        );
      }
      this.#resetSelected();
    }

    if (this.#externalDropzoneSelected && this.#draggableSelected) {
      const dropZone = this.#draggableSelected.parentNode as DropZoneComponent;
      this.#externalDropzoneSelected.appendChild(this.#draggableSelected);
      dropZone.dispatchEvent(
        new DraggableComponentEvent('draggable-left', this.#draggableSelected),
      );
      this.#resetSelected();
    }
  }

  #checkSelections() {
    if (this.#draggableSelected && this.#dropzoneSelected) {
      if (this.#dropzoneSelected.draggableComponents[0]) {
        this.#dropzoneSelected.broadCastEvent(
          'limitExceeded',
          this.#dropzoneSelected.draggableComponents[0],
        );
      }

      const currentDropzone = this.#draggableSelected
        .parentNode as DropZoneComponent;

      if (currentDropzone) {
        currentDropzone.dispatchEvent(
          new DraggableComponentEvent(
            'draggable-left',
            this.#draggableSelected,
          ),
        );
      }

      if (this.#draggableSelected.hasDuplicates) {
        const duplicate = this.#draggableSelected.cloneNode(
          true,
        ) as DraggableComponent;
        duplicate.hasDuplicates = true;
        this.#dropzoneSelected?.dropzoneService.setValue(duplicate);
      } else {
        this.#dropzoneSelected?.dropzoneService.setValue(
          this.#draggableSelected,
        );
      }

      this.#resetSelected();
    }
  }

  #swapDropzoneContents(secondDropzone: DropZoneComponent) {
    if (!this.#dropzoneSelected) {
      return;
    }

    const firstDraggable = this.#dropzoneSelected?.draggableComponents[0];
    const secondDraggable = secondDropzone?.draggableComponents[0];

    if (firstDraggable || secondDraggable) {
      this.#dropzoneSelected.dropzoneService.setValue(secondDraggable || null);
      secondDropzone.dropzoneService.setValue(firstDraggable || null);
      secondDropzone.setSelected(false);

      if (!firstDraggable) {
        secondDropzone.dispatchEvent(
          new DraggableComponentEvent('draggable-left', firstDraggable),
        );
      }

      if (!secondDraggable) {
        this.#dropzoneSelected.dispatchEvent(
          new DraggableComponentEvent('draggable-left', secondDraggable),
        );
      }

      this.#resetSelected();
    } else {
      this.#dropzoneSelected = secondDropzone;
    }
  }

  #resetSelected() {
    this.#draggableSelected?.setSelected(false);
    this.#dropzoneSelected?.setSelected(false);

    if (
      this.#externalDropzoneSelected &&
      this.#isSelectableElement(this.#externalDropzoneSelected)
    ) {
      this.#externalDropzoneSelected.setSelected(false);
    }

    this.#draggableSelected = null;
    this.#dropzoneSelected = null;
    this.#externalDropzoneSelected = null;
  }

  override render() {
    return html` <slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-drag-drop': DragDropComponent;
  }
}
